// <before-after> — a premium before/after image comparison slider.
// Self-registering, dependency-free custom element. Config is read from a
// <script type="application/json" data-ba-config> tag inside the element.
//
// Source of truth for the divider position is a visually-hidden native
// <input type="range" min=0 max=100> (class .ba__range): it gives keyboard
// (arrows/Home/End), focus and RTL-correct key direction for free. JS keeps
// input.value <-> the --ba-pos custom property in sync and mirrors both to the
// clip-path and the handle. Position is stored as a PERCENT (0..100) measured
// from the inline-start (horizontal) or block-start (vertical), so the clip
// self-corrects on resize with no cached pixel rects.
//
// Orientation (horizontal/vertical), interaction mode (drag/hover/click),
// drag-anywhere, intro-sweep, idle peek-hint, live readout and an optional
// spotlight/lens reveal are all driven by config. All ambient motion is gated
// behind prefers-reduced-motion.

class BeforeAfter extends HTMLElement {
  connectedCallback() {
    // No re-init guard: disconnectedCallback always tears down fully before a
    // subsequent connectedCallback can fire for this instance (custom element
    // lifecycle guarantee), so it's safe — and necessary — to rebuild fully
    // every time (theme-editor reconnect must rebuild observers + replay intro).
    this.cfg = this._readConfig();
    this.rtl = (this.getAttribute('dir') || '').toLowerCase() === 'rtl';
    this.vertical = this.cfg.orientation === 'vertical';
    this.mode = this.cfg.mode || 'drag';
    this.spotlight = this.cfg.reveal === 'spotlight';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.stage = this.querySelector('.ba__stage');
    this.range = this.querySelector('.ba__range');
    this.readout = this.querySelector('.ba__readout');
    this.handle = this.querySelector('.ba__handle');
    if (!this.stage) return;

    this.pos = this._clamp(this.cfg.start);
    this._pendingPos = this.pos;
    this._interacted = false;
    this.dragging = false;
    this.hovering = false;

    // In spotlight mode the range still drives the lens (along the axis that
    // matches orientation) so keyboard/AT users retain full operability —
    // it stays enabled and exposed instead of being disabled + aria-hidden.
    if (this.spotlight) {
      const physicalX = this.vertical ? 50 : (this.rtl ? 100 - this.pos : this.pos);
      const physicalY = this.vertical ? this.pos : 50;
      this.stage.style.setProperty('--ba-lx', physicalX + '%');
      this.stage.style.setProperty('--ba-ly', physicalY + '%');
      if (this.range) this.range.setAttribute('aria-valuetext', Math.round(this.pos) + '%');
    }

    this._bind();
    this._bindReducedMotion();

    if (this.spotlight) {
      // nothing to sweep — lens simply follows the pointer (and the keyboard, via the range)
    } else {
      this._write(this.pos, true);
      this._maybeIntro();
      this._maybePeek();
    }
  }

  disconnectedCallback() {
    this._teardown();
  }

  // ---------- config ----------
  _readConfig() {
    const el = this.querySelector('[data-ba-config]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  _clamp(n) {
    n = parseFloat(n);
    if (isNaN(n)) return 50;
    return n < 0 ? 0 : n > 100 ? 100 : n;
  }

  // ---------- position write (batched via rAF for pointer streams) ----------
  // `immediate` skips the eased CSS transition for one-off instant sets (the
  // initial paint, and the idle-peek nudge out/back) — live pointer drag and
  // hover-follow already skip it via the .ba--live class instead.
  _write(pos, immediate) {
    pos = this._clamp(pos);
    this.pos = pos;
    const rounded = Math.round(pos);
    if (immediate) {
      const prev = this.stage.style.transition;
      this.stage.style.transition = 'none';
      this.stage.style.setProperty('--ba-pos', String(pos));
      void this.stage.offsetWidth; // flush so the value lands before easing is restored
      this.stage.style.transition = prev;
    } else {
      this.stage.style.setProperty('--ba-pos', String(pos));
    }
    if (this.range && !this.range.disabled) {
      // setting .value programmatically does NOT fire 'input', so no feedback loop
      this.range.value = String(rounded);
      this.range.setAttribute('aria-valuetext', rounded + '%');
    }
    if (this.readout) this.readout.textContent = rounded + '%';
  }

  // ---------- spotlight: keyboard-driven lens position (range -> lens axis) ----------
  _writeSpotlightAxis(pos) {
    pos = this._clamp(pos);
    this.pos = pos;
    const rounded = Math.round(pos);
    if (this.vertical) {
      this.stage.style.setProperty('--ba-ly', pos + '%');
    } else {
      // the range's logical value already applied RTL-correct key direction;
      // convert it back to a physical x for the (always-physical) lens position
      const physical = this.rtl ? 100 - pos : pos;
      this.stage.style.setProperty('--ba-lx', physical + '%');
    }
    if (this.range) this.range.setAttribute('aria-valuetext', rounded + '%');
  }

  _schedule() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      this._write(this._pendingPos);
    });
  }

  // ---------- pointer -> percent ----------
  _fromPointer(e) {
    const r = this.stage.getBoundingClientRect(); // fresh every move — never cached across a resize
    let ratio;
    if (this.vertical) {
      ratio = r.height ? (e.clientY - r.top) / r.height : 0.5;
    } else if (this.rtl) {
      ratio = r.width ? (r.right - e.clientX) / r.width : 0.5; // RTL: measure from the right (inline-start)
    } else {
      ratio = r.width ? (e.clientX - r.left) / r.width : 0.5;
    }
    return this._clamp(ratio * 100);
  }

  // ---------- interaction state ----------
  _setLive(on) {
    this.classList.toggle('ba--live', on);
  }

  _markInteracted() {
    if (this._interacted) return;
    this._interacted = true;
    this._cancelPeek();
  }

  // ---------- events ----------
  _bind() {
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onEnter = this._onEnter.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._onInput = this._onInput.bind(this);
    // block the browser's native image drag ghost inside the stage
    this._onDragStart = (e) => e.preventDefault();

    this.stage.addEventListener('pointerdown', this._onDown);
    this.stage.addEventListener('pointermove', this._onMove);
    this.stage.addEventListener('pointerup', this._onUp);
    this.stage.addEventListener('pointercancel', this._onUp);
    this.stage.addEventListener('pointerenter', this._onEnter);
    this.stage.addEventListener('pointerleave', this._onLeave);
    this.stage.addEventListener('dragstart', this._onDragStart);

    if (this.range) this.range.addEventListener('input', this._onInput);

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => {
        clearTimeout(this._rt);
        // percent-based clip self-corrects, but re-commit to keep the hidden
        // range thumb + readout consistent after a size change (debounced)
        this._rt = setTimeout(() => { if (!this.spotlight) this._write(this.pos); }, 120);
      });
      this._ro.observe(this.stage);
    }
  }

  // prefers-reduced-motion can change at runtime (OS-level toggle); keep
  // `this.reduced` current instead of a stale snapshot from mount time
  _bindReducedMotion() {
    this._reducedMql = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._onReducedChange = (e) => { this.reduced = e.matches; };
    if (this._reducedMql.addEventListener) {
      this._reducedMql.addEventListener('change', this._onReducedChange);
    } else if (this._reducedMql.addListener) {
      this._reducedMql.addListener(this._onReducedChange); // Safari < 14 fallback
    }
  }

  _onDown(e) {
    if (e.button != null && e.button !== 0) return; // primary button / touch / pen only
    this._markInteracted();
    this._stopIntro();

    if (this.spotlight) {
      this._spotActive = true;
      this._scheduleLens(e);
      try { this.stage.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      return;
    }

    if (this.mode === 'drag') {
      const onHandle = this.handle && (e.target === this.handle || this.handle.contains(e.target));
      if (!this.cfg.dragAnywhere && !onHandle) return;
      this.dragging = true;
      this._activePointer = e.pointerId;
      this._setLive(true);
      try { this.stage.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      e.preventDefault();
      this._pendingPos = this._fromPointer(e);
      this._schedule();
    } else if (this.mode === 'click') {
      // eased jump (transition stays on because we are NOT live)
      this._pendingPos = this._fromPointer(e);
      this._schedule();
    }
    // hover mode ignores pointerdown; it tracks pointermove while hovering
  }

  _onMove(e) {
    if (this.spotlight) {
      if (this._spotActive) this._scheduleLens(e);
      return;
    }
    if (this.dragging) {
      this._pendingPos = this._fromPointer(e);
      this._schedule();
    } else if (this.mode === 'hover' && this.hovering) {
      this._markInteracted();
      this._stopIntro();
      this._pendingPos = this._fromPointer(e);
      this._schedule();
    }
  }

  _onUp(e) {
    if (this.spotlight) {
      // stay active while the pointer is still over the stage so the lens
      // keeps following pointermove after a click-release (only a real
      // pointerleave should stop it — see _onLeave)
      this._spotActive = this.hovering;
      try { this.stage.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
      return;
    }
    if (!this.dragging) return;
    this.dragging = false;
    this._setLive(false);
    try { this.stage.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
  }

  _onEnter(e) {
    this.hovering = true;
    if (this.spotlight) { this._spotActive = true; this._scheduleLens(e); return; }
    if (this.mode === 'hover') this._setLive(true); // tight 1:1 tracking, no transition
  }

  _onLeave() {
    this.hovering = false;
    if (this.spotlight) { this._spotActive = false; return; }
    if (this.mode === 'hover' && !this.dragging) this._setLive(false);
  }

  _onInput() {
    // keyboard (arrows/Home/End) or AT — the native input already applied RTL
    // key mapping and clamping; mirror its value to the clip (or, in spotlight
    // mode, to the lens's primary axis). Transition stays on (not live) so key
    // presses ease smoothly.
    this._markInteracted();
    this._stopIntro();
    if (this.spotlight) {
      this._writeSpotlightAxis(this.range.value);
    } else {
      this._write(this.range.value);
    }
  }

  // ---------- spotlight / lens ----------
  _scheduleLens(e) {
    this._lensE = e;
    if (this._lraf) return;
    this._lraf = requestAnimationFrame(() => {
      this._lraf = 0;
      const ev = this._lensE;
      const r = this.stage.getBoundingClientRect();
      const x = r.width ? ((ev.clientX - r.left) / r.width) * 100 : 50;
      const y = r.height ? ((ev.clientY - r.top) / r.height) * 100 : 50;
      const lx = this._clampPct(x);
      const ly = this._clampPct(y);
      this.stage.style.setProperty('--ba-lx', lx + '%');
      this.stage.style.setProperty('--ba-ly', ly + '%');

      // keep the keyboard-operable range in sync with pointer-driven moves
      // (along its primary axis) so arrow keys continue from wherever the
      // pointer left the lens, instead of jumping from a stale value
      const physicalPrimary = this.vertical ? ly : lx;
      this.pos = this._clamp(this.vertical ? physicalPrimary : (this.rtl ? 100 - physicalPrimary : physicalPrimary));
      if (this.range) {
        const rounded = Math.round(this.pos);
        this.range.value = String(rounded);
        this.range.setAttribute('aria-valuetext', rounded + '%');
      }
    });
  }

  _clampPct(n) { return n < 0 ? 0 : n > 100 ? 100 : n; }

  // ---------- intro sweep (one-shot, on first view) ----------
  _maybeIntro() {
    if (!this.cfg.intro || this.reduced) return;
    const from = this.pos < 50 ? 100 : 0; // sweep from the farther edge to the start position

    // pre-position at the edge instantly (transition skipped via the
    // `immediate` write, not .ba--live — that class also flips on will-change,
    // which must stay off while the element is just parked below the fold)
    // so it's already parked there before it scrolls into view — no visible snap.
    this._write(from, true);

    const ease = () => {
      if (this._interacted) { this._setLive(false); return; }
      this._setLive(false);            // re-enable the transition
      this.classList.add('ba--intro'); // longer eased duration
      void this.stage.offsetWidth;     // flush 'from' as the transition baseline
      this._write(this.cfg.start);     // ease from -> start
      this._introTimer = setTimeout(() => this.classList.remove('ba--intro'), 1100);
    };
    const trigger = () => requestAnimationFrame(ease);

    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver((entries) => {
        for (const en of entries) {
          if (en.isIntersecting) {
            this._stopIntroObserver();
            trigger();
            break;
          }
        }
      }, { threshold: 0.35 });
      this._io.observe(this.stage);
    } else {
      trigger();
    }
  }

  _stopIntroObserver() {
    if (this._io) { this._io.disconnect(); this._io = null; }
  }

  // called when the user takes over — cancel a pending/queued intro cleanly
  _stopIntro() {
    this._stopIntroObserver();
    if (this._introTimer) { clearTimeout(this._introTimer); this._introTimer = null; }
    this.classList.remove('ba--intro');
  }

  // ---------- idle peek hint (single subtle nudge) ----------
  _maybePeek() {
    if (!this.cfg.idlePeek || this.cfg.intro || this.reduced) return;
    this._peekTimer = setTimeout(() => {
      if (this._interacted) return;
      const dir = this.pos > 50 ? -1 : 1;
      const amount = 9;
      this._write(this._clamp(this.cfg.start + dir * amount), true); // instant nudge out
      this._peekTimer2 = setTimeout(() => {
        if (this._interacted) return;
        this._write(this.cfg.start, true); // instant nudge back
      }, 520);
    }, 1400);
  }

  _cancelPeek() {
    clearTimeout(this._peekTimer);
    clearTimeout(this._peekTimer2);
  }

  // ---------- cleanup ----------
  // Full teardown is what makes it safe to fully re-run connectedCallback on
  // reconnect (e.g. the theme editor moving/re-inserting the section): every
  // listener/observer/timer started in connectedCallback must be undone here.
  _teardown() {
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._lraf) cancelAnimationFrame(this._lraf);
    this._stopIntro();
    this._cancelPeek();
    clearTimeout(this._rt);
    if (this._ro) { this._ro.disconnect(); this._ro = null; }

    if (this.stage) {
      this.stage.removeEventListener('pointerdown', this._onDown);
      this.stage.removeEventListener('pointermove', this._onMove);
      this.stage.removeEventListener('pointerup', this._onUp);
      this.stage.removeEventListener('pointercancel', this._onUp);
      this.stage.removeEventListener('pointerenter', this._onEnter);
      this.stage.removeEventListener('pointerleave', this._onLeave);
      if (this._onDragStart) this.stage.removeEventListener('dragstart', this._onDragStart);
    }
    if (this.range && this._onInput) this.range.removeEventListener('input', this._onInput);

    if (this._reducedMql) {
      if (this._reducedMql.removeEventListener) this._reducedMql.removeEventListener('change', this._onReducedChange);
      else if (this._reducedMql.removeListener) this._reducedMql.removeListener(this._onReducedChange);
      this._reducedMql = null;
    }

    this._setLive(false);
    this.classList.remove('ba--intro');
  }
}

if (!customElements.get('before-after')) {
  customElements.define('before-after', BeforeAfter);
}
